/**
 * Utility functions for handling month and trimester values
 * Used to safely handle month values that can be either numeric (1-12) or trimester strings ("T1"-"T4")
 */

/**
 * Type guard to check if a month value is a trimester string
 * @param month - The month value to check (can be number or string)
 * @returns True if the value is a trimester string (T1-T4)
 */
export function isTrimester(month: number | string | undefined): month is string {
  return typeof month === "string" && /^T[1-4]$/.test(month);
}

/**
 * Converts a month/trimester value to a numeric month for comparison and sorting
 * @param month - The month value (1-12 or "T1"-"T4")
 * @param mode - Which month to return for trimesters: 'start' (first month), 'middle', or 'end' (last month)
 * @returns A numeric month value (1-12)
 */
export function monthToNumeric(
  month: number | string,
  mode: "start" | "middle" | "end" = "start",
): number {
  // If it's already a number, return it
  if (typeof month === "number") {
    return month;
  }

  // If it's a trimester string, convert it to a representative month
  if (isTrimester(month)) {
    const trimesterNum = parseInt(month.substring(1));
    return trimesterToMonth(trimesterNum, mode);
  }

  // If it's a numeric string, parse it
  const parsed = parseInt(month);
  if (!isNaN(parsed) && parsed >= 1 && parsed <= 12) {
    return parsed;
  }

  // Fallback - return 1 if we can't parse it
  console.warn(`Unable to convert month value to numeric: ${month}`);
  return 1;
}

/**
 * Converts a trimester number to a month based on the mode
 * @param trimester - The trimester number (1-4)
 * @param mode - Which month to return: 'start', 'middle', or 'end'
 * @returns A numeric month value (1-12)
 */
function trimesterToMonth(
  trimester: number,
  mode: "start" | "middle" | "end" = "start",
): number {
  // Trimester ranges:
  // T1: Jan (1), Feb (2), Mar (3)
  // T2: Apr (4), May (5), Jun (6)
  // T3: Jul (7), Aug (8), Sep (9)
  // T4: Oct (10), Nov (11), Dec (12)

  const startMonth = (trimester - 1) * 3 + 1;

  switch (mode) {
    case "start":
      return startMonth; // First month of trimester
    case "middle":
      return startMonth + 1; // Middle month of trimester
    case "end":
      return startMonth + 2; // Last month of trimester
    default:
      return startMonth;
  }
}

/**
 * Gets the month range for a trimester
 * @param trimester - The trimester string ("T1"-"T4") or number (1-4)
 * @returns Object with start and end month numbers
 */
export function trimesterToMonthRange(
  trimester: string | number,
): { start: number; end: number } {
  const trimesterNum =
    typeof trimester === "string" ? parseInt(trimester.substring(1)) : trimester;

  const start = (trimesterNum - 1) * 3 + 1;
  const end = start + 2;

  return { start, end };
}

/**
 * Calculates which trimester a numeric month belongs to
 * @param month - The month number (1-12)
 * @returns The trimester number (1-4)
 */
export function monthToTrimester(month: number): number {
  if (month < 1 || month > 12) {
    console.warn(`Invalid month number: ${month}`);
    return 1;
  }
  return Math.floor((month - 1) / 3) + 1;
}

/**
 * Gets a French label for a month or trimester
 * @param month - The month value (1-12 or "T1"-"T4")
 * @param monthNamesFr - Optional array of French month names
 * @returns A French label string
 */
export function getMonthLabel(
  month: number | string,
  monthNamesFr?: string[],
): string {
  const defaultMonthNames = [
    "janvier",
    "février",
    "mars",
    "avril",
    "mai",
    "juin",
    "juillet",
    "août",
    "septembre",
    "octobre",
    "novembre",
    "décembre",
  ];

  const names = monthNamesFr || defaultMonthNames;

  if (isTrimester(month)) {
    const trimesterNum = parseInt(month.substring(1));
    return `Trimestre ${trimesterNum}`;
  }

  const monthNum =
    typeof month === "number" ? month : parseInt(month);

  if (monthNum >= 1 && monthNum <= 12) {
    return names[monthNum - 1];
  }

  return "Période invalide";
}

/**
 * Creates a sort key for month values that handles both numeric months and trimesters
 * Useful for sorting arrays that contain mixed month/trimester values
 * @param month - The month value (1-12 or "T1"-"T4")
 * @returns A numeric value suitable for sorting (1-12)
 */
export function getMonthSortKey(month: number | string): number {
  return monthToNumeric(month, "start");
}
